import { useMemo, useState } from "react";
import { Search } from "lucide-react";
import SEO from "@/components/SEO";
import Overline from "@/components/Overline";
import ProductCard from "@/components/ProductCard";
import { activeProducts, categories } from "@/data/products";
import { seo } from "@/config/site";

export default function ProductsIndex() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const products = activeProducts();
  const cats = ["All", ...categories.map((c) => c.name)];

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return products.filter((p) => {
      const inCat = cat === "All" || p.category === cat;
      const inSearch = !query || p.name.toLowerCase().includes(query) || p.shortDescription.toLowerCase().includes(query);
      return inCat && inSearch;
    });
  }, [q, cat, products]);

  return (
    <>
      <SEO
        title="Packaging Products"
        description="Explore corrugated boxes, mono cartons, PP bins, stretch film, silica gel, strapping rolls, foam fitments, pallets, activated carbon and more."
        canonical={`${seo.siteUrl}/products`}
      />
      <section className="border-b border-neutral-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <Overline>Catalogue</Overline>
          <h1 className="font-heading mt-4 text-4xl sm:text-5xl font-bold tracking-tight text-neutral-900">Packaging Products</h1>
          <p className="mt-4 text-neutral-600 max-w-2xl">Explore our range of packaging products and material-handling solutions. Configurations are discussed based on requirement.</p>
        </div>
      </section>

      <section className="py-10 md:py-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-8">
            <div className="relative w-full lg:w-96">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" />
              <input
                type="search"
                placeholder="Search products…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="w-full pl-10 pr-3 py-3 border border-neutral-300 bg-white text-neutral-900 focus:border-[#A91D22] focus:ring-1 focus:ring-[#A91D22] outline-none rounded-sm text-sm"
                data-testid="products-search"
                aria-label="Search products"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              {cats.map((c) => (
                <button
                  key={c}
                  onClick={() => setCat(c)}
                  className={`px-3 py-2 text-xs font-medium border rounded-sm transition-colors ${cat === c ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 text-neutral-700 hover:border-neutral-900"}`}
                  data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>

          {filtered.length === 0 ? (
            <div className="border border-dashed border-neutral-300 py-16 text-center text-neutral-500">
              No products match your search.
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="products-grid">
              {filtered.map((p) => (
                <ProductCard key={p.slug} product={p} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
